import axiosInstance from 'utils/axios';
import { uploadAttachments, DocumentUploadFeature } from '../documentUploadService';

/**
 * Builds FormData for creating/updating a Toolbox Talk to support ASP.NET Core model binding
 * of complex collections (Attendees, Documents) via multipart/form-data.
 */
const buildToolboxTalkFormData = (request, isEdit = false) => {
  const formData = new FormData();

  formData.append('JobId', request.JobId || '');
  formData.append('Topic', request.Topic || '');
  formData.append('TalkDate', request.TalkDate || '');
  formData.append('TalkTime', request.TalkTime || '');
  formData.append('SupervisorId', request.SupervisorId || '');

  if (request.StatusId) {
    formData.append('StatusId', request.StatusId);
  }
  if (request.Notes) {
    formData.append('Notes', request.Notes);
  }
  if (request.DiscussionPoints) {
    formData.append('DiscussionPoints', request.DiscussionPoints);
  }

  // 1. Nested collection: Attendees [Attendees[i].UserId, etc.]
  if (request.Attendees && request.Attendees.length > 0) {
    request.Attendees.forEach((attendee, index) => {
      formData.append(`Attendees[${index}].UserId`, attendee.UserId);
      formData.append(`Attendees[${index}].AttendanceConfirmed`, attendee.AttendanceConfirmed !== false ? 'true' : 'false');
      if (attendee.SignatureUrl) {
        formData.append(`Attendees[${index}].SignatureUrl`, attendee.SignatureUrl);
      }
      if (attendee.SignedAt) {
        formData.append(`Attendees[${index}].SignedAt`, attendee.SignedAt);
      }
    });
  }

  // 2. File list and tracking deletions
  if (isEdit) {
    formData.append('ToolboxTalkId', request.ToolboxTalkId);
    
    if (request.NewDocuments && request.NewDocuments.length > 0) {
      request.NewDocuments.forEach((file) => {
        formData.append('NewDocuments', file);
      });
    }
    if (request.DeletedDocumentIds && request.DeletedDocumentIds.length > 0) {
      request.DeletedDocumentIds.forEach((id) => {
        formData.append('DeletedDocumentIds', id);
      });
    }
  } else {
    if (request.Documents && request.Documents.length > 0) {
      request.Documents.forEach((file) => {
        formData.append('Documents', file);
      });
    }
  }

  return formData;
};

// Fetch paginated, filtered talks
export const getToolboxTalks = async (params) => {
  try {
    const response = await axiosInstance.get('/api/WHS/ToolboxTalks', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Create a new toolbox talk
export const createToolboxTalk = async (data) => {
  try {
    const rawFiles = (data.Documents || []).filter(f => f instanceof File);
    let attachmentsPayload = [];

    if (rawFiles.length > 0) {
      const uploadRes = await uploadAttachments(
        DocumentUploadFeature.TOOLBOX_TALK_ATTACHMENT,
        rawFiles
      );
      if (!uploadRes.success) {
        throw new Error(uploadRes.message || 'Failed to upload attachments.');
      }
      attachmentsPayload = (uploadRes.data || []).map((fileMeta, idx) => {
        const bytes = rawFiles[idx]?.size || rawFiles.find(rf => rf.name === fileMeta.fileName)?.size || (fileMeta.fileSizeKB ? fileMeta.fileSizeKB * 1024 : 0);
        return {
          FileName: fileMeta.fileName,
          FilePath: fileMeta.filePath,
          FileExtension: fileMeta.fileExtension,
          FileSize: bytes,
          FileSizeBytes: bytes
        };
      });
    }

    const payload = {
      JobId: data.JobId,
      CompanyId: data.CompanyId || data.companyId || null,
      UserId: data.UserId || data.userId || null,
      Topic: data.Topic,
      TalkDate: data.TalkDate,
      TalkTime: data.TalkTime,
      StatusId: data.StatusId || null,
      DiscussionPoints: data.DiscussionPoints || null,
      Notes: data.Notes || null,
      SupervisorId: data.SupervisorId,
      Attendees: data.Attendees || [],
      Attachments: attachmentsPayload
    };

    const companyIdParam = data.CompanyId || data.companyId || undefined;
    const userIdParam = data.UserId || data.userId || undefined;

    const response = await axiosInstance.post('/api/WHS/ToolboxTalks', payload, {
      headers: { 'Content-Type': 'application/json' },
      params: {
        ...(companyIdParam && { companyId: companyIdParam }),
        ...(userIdParam && { userId: userIdParam }),
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update an existing toolbox talk
export const updateToolboxTalk = async (id, data) => {
  try {
    const rawFiles = (data.NewDocuments || []).filter(f => f instanceof File);
    let newAttachmentsPayload = [];

    if (rawFiles.length > 0) {
      const uploadRes = await uploadAttachments(
        DocumentUploadFeature.TOOLBOX_TALK_ATTACHMENT,
        rawFiles
      );
      if (!uploadRes.success) {
        throw new Error(uploadRes.message || 'Failed to upload new attachments.');
      }
      newAttachmentsPayload = (uploadRes.data || []).map((fileMeta, idx) => {
        const bytes = rawFiles[idx]?.size || rawFiles.find(rf => rf.name === fileMeta.fileName)?.size || (fileMeta.fileSizeKB ? fileMeta.fileSizeKB * 1024 : 0);
        return {
          FileName: fileMeta.fileName,
          FilePath: fileMeta.filePath,
          FileExtension: fileMeta.fileExtension,
          FileSize: bytes,
          FileSizeBytes: bytes
        };
      });
    }

    const payload = {
      ToolboxTalkId: id,
      JobId: data.JobId,
      Topic: data.Topic,
      TalkDate: data.TalkDate,
      TalkTime: data.TalkTime,
      StatusId: data.StatusId || null,
      DiscussionPoints: data.DiscussionPoints || null,
      Notes: data.Notes || null,
      SupervisorId: data.SupervisorId,
      Attendees: data.Attendees || [],
      NewAttachments: newAttachmentsPayload,
      DeletedDocumentIds: data.DeletedDocumentIds || []
    };

    const response = await axiosInstance.post(`/api/WHS/ToolboxTalks/${id}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch attendees for a talk
export const getToolboxTalkAttendees = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/WHS/ToolboxTalks/${id}/Attendees`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch documents referenced in a talk
export const getToolboxTalkDocuments = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/WHS/ToolboxTalks/${id}/Documents`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Upload a single new document to an existing talk
export const addToolboxDocument = async (id, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(`/api/WHS/ToolboxTalks/${id}/Documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a document from a talk
export const deleteToolboxDocument = async (id, docId) => {
  try {
    const response = await axiosInstance.post(`/api/WHS/ToolboxTalks/${id}/Documents/${docId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Fetch a single toolbox talk by ID
export const getToolboxTalkById = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/WHS/ToolboxTalk/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a toolbox talk
export const deleteToolboxTalk = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/WHS/DeleteToolboxTalk/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

